import { LoggerService } from '@angular-ru/cdk/logger';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { delay, filter, take } from 'rxjs';
import { IDialogMediaLock } from 'src/app/api/modules/core/components/dialog/IDialogMediaLock';
import { IButton } from 'src/app/api/modules/core/dynamic/components/IButton';
import { IMediaLockable } from 'src/app/api/modules/core/dynamic/components/IMediaLock';
import { createDigitaServiceError } from 'src/app/app-error';
import { flexContainerStyle } from 'src/app/util/FlexLayoutTokens';
import { MediaLockableComponent } from '../../components/static/media-lockable/media-lockable.component';
import { ButtonComponent } from '../../dynamic/components/button/button.component';

/**
 * The validated, template-ready view of an {@link IDialogMediaLock} configuration.
 */
interface ValidatedDialogMediaLock {
  /**
   * The optional dialog title.
   */
  header?: string;

  /**
   * The media lock to display.
   */
  lock: IMediaLockable;

  /**
   * The close button revealed once the media is unlocked.
   */
  negativeButton: IButton;

  /**
   * The footer's legacy flex-layout tokens translated to inline CSS.
   */
  footerStyle: Record<string, string>;
}

/**
 * Validates the dialog configuration, throwing a fatal error on contract violations.
 */
function validateConfiguration(configuration: IDialogMediaLock | null): ValidatedDialogMediaLock {
  // if there is no configuration then that is an error
  if (!configuration) {
    throw createDigitaServiceError(`DialogMediaLock`, `applyConfiguration`, `No configuration provided but this is required.`, `config`);
  }

  // if there is no lock then that is an error
  const lock = configuration.lock;
  if (!lock) {
    throw createDigitaServiceError(`DialogMediaLock`, `applyConfiguration`, `No "lock" property provided but this is required.`, `config`);
  }

  // the footer with its negative button is required; a positive button is not supported
  const footer = configuration.footer;
  if (!footer) {
    throw createDigitaServiceError(
      `DialogMediaLock`,
      `applyConfiguration`,
      `No "footer" property provided but this is required.`,
      `config`,
    );
  }
  if (!footer.negativeButton) {
    throw createDigitaServiceError(
      `DialogMediaLock`,
      `applyConfiguration`,
      `No "footer.negativeButton" property provided but this is required.`,
      `config`,
    );
  }
  if (footer.positiveButton) {
    throw createDigitaServiceError(
      `DialogMediaLock`,
      `applyConfiguration`,
      `Do not use "footer.positiveButton" property for this dialog type. Use "footer.negativeButton" instead.`,
      `config`,
    );
  }

  return {
    header: configuration.header,
    lock,
    negativeButton: footer.negativeButton,
    footerStyle: flexContainerStyle(footer.layout || 'row', footer.layoutAlign || 'center center'),
  };
}

/**
 * Dialog Media Lock is a special kind of Dialog used to contain a {@link MediaLockableComponent}.
 *
 * They have a title, a MediaLockable, and a close button revealed on unlock
 * (or on error, so the user is never stranded).
 *
 * See {@link IDialogMediaLock}
 */
@Component({
  selector: 'app-dialog-media-lock',
  templateUrl: './dialog-media-lock.component.html',
  styleUrls: ['./dialog-media-lock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MediaLockableComponent,
    MatDialogActions,
    // There is a circular dependency here, so we need to use forwardRef
    forwardRef(() => ButtonComponent),
  ],
})
export class DialogMediaLockComponent {
  private readonly dialogRef = inject<MatDialogRef<DialogMediaLockComponent, boolean>>(MatDialogRef);
  private readonly loggerService = inject(LoggerService);

  /**
   * The validated configuration; the {@link DialogService} supplies it via MAT_DIALOG_DATA.
   */
  protected readonly config = validateConfiguration(inject<IDialogMediaLock | null>(MAT_DIALOG_DATA));

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // STATE
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Has the media lock been unlocked?
   */
  private readonly unlocked = signal(false);

  /**
   * Has the media lock errored?
   */
  private readonly error = signal(false);

  /**
   * The close button appears 500ms after the unlock so it doesn't pop in the
   * same instant the media completes.
   */
  private readonly unlockedDelayed = toSignal(
    toObservable(this.unlocked).pipe(
      filter((unlocked) => unlocked),
      take(1),
      delay(500),
    ),
    { initialValue: false },
  );

  /**
   * Should the close button be shown? Unlocks reveal it after the settle
   * delay; errors reveal it immediately so the user is never stranded.
   */
  protected readonly showButton = computed(() => this.unlockedDelayed() || this.error());

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // MEDIA LOCK EVENTS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Occurs from the Media Lock when the Media is Unlocked.
   */
  mediaLockUnlocked() {
    this.unlocked.set(true);
  }

  /**
   * Occurs from the Media Lock when the Media has experienced an error.
   */
  mediaLockError() {
    this.loggerService.error(`Media Error - Unlocked`);
    this.error.set(true);
  }

  /**
   * Occurs when the user clicks on the close (negative) button.
   */
  onNegativeClick() {
    this.dialogRef.close(false);
  }
}
